import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { communityApi } from '../lib/api';
import { validateQuestion, apiErrorMessage } from '../lib/communityValidation';
import QuestionFormFields from '../components/community/QuestionFormFields';
import { usePageSeo } from '../hooks/usePageSeo';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { MessageSquare, User, Calendar, Plus, LogIn, ChevronUp, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';

const PAGE_SIZE = 12;

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'unanswered', label: 'Unanswered' },
  { value: 'solved', label: 'Solved' },
];

const SORTS = [
  { value: 'newest', label: 'Newest' },
  { value: 'top', label: 'Top' },
];

const EMPTY_FORM = { title: '', description: '', category: 'general' };

const CommunityPage = () => {
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || null;
  const status = searchParams.get('status') || 'all';
  const sort = searchParams.get('sort') || 'newest';

  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  usePageSeo(
    'Community Q&A — Automotive Engineering Questions | AUTOXIUM',
    'Ask and answer automotive engineering questions: diagnostics, EV systems, tools, and careers.',
  );

  const loadCategories = useCallback(async () => {
    try {
      setCategories(await communityApi.getCategories());
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const fetchQuestions = useCallback(async (skip) => {
    const data = await communityApi.getQuestions({ skip, limit: PAGE_SIZE, category, status, sort });
    setTotal(data.total);
    setQuestions((prev) => (skip === 0 ? data.items : [...prev, ...data.items]));
  }, [category, status, sort]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await fetchQuestions(0);
      } catch (err) {
        console.error('Error fetching questions:', err);
        setError('Failed to load questions');
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchQuestions]);

  const updateParam = (key, value, defaultValue) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      await fetchQuestions(questions.length);
    } catch (err) {
      console.error('Error loading more questions:', err);
      toast.error('Failed to load more questions');
    } finally {
      setLoadingMore(false);
    }
  };

  const openAskDialog = (open) => {
    setDialogOpen(open);
    if (open) {
      setFormData({ ...EMPTY_FORM, category: category || 'general' });
      setFormErrors({});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateQuestion(formData);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;
    setSubmitting(true);
    try {
      await communityApi.createQuestion({
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
      });
      toast.success('Question posted');
      setDialogOpen(false);
      await Promise.all([fetchQuestions(0), loadCategories()]);
    } catch (err) {
      console.error('Error creating question:', err);
      toast.error(apiErrorMessage(err, 'Failed to post question'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const categoryLabel = (key) => categories.find((c) => c.key === key)?.label || key;
  const allCount = categories.reduce((sum, c) => sum + c.question_count, 0);
  const activeCategory = categories.find((c) => c.key === category);
  const hasMore = questions.length < total;

  const chipClass = (active) =>
    `px-3 py-1.5 rounded-sm border text-xs font-bold uppercase tracking-wider transition-colors ${
      active
        ? 'bg-chassis text-white border-chassis'
        : 'border-structure text-muted-foreground hover:border-autoxium-blue hover:text-autoxium-blue'
    }`;

  return (
    <div className="min-h-screen" data-testid="community-page">
      {/* Hero Section */}
      <section className="bg-chassis text-white py-16 md:py-24">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-4">
                Community Discussions
              </p>
              <h1 className="font-chivo text-4xl md:text-6xl font-black uppercase tracking-tighter mb-6">
                Community
              </h1>
              <p className="text-lg text-white/70 max-w-xl leading-relaxed">
                Ask questions, share knowledge, and get answers from fellow automotive enthusiasts and experts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions List */}
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <h2 className="font-chivo text-2xl font-bold uppercase tracking-tight">
              {activeCategory ? activeCategory.label : 'Questions'}
            </h2>
            {isAuthenticated ? (
              <Dialog open={dialogOpen} onOpenChange={openAskDialog}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-autoxium-blue hover:bg-autoxium-blue/90 text-white font-bold uppercase tracking-wider"
                    data-testid="ask-question-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ask a Question
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="ask-question-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-chivo uppercase">Ask a Question</DialogTitle>
                    <DialogDescription>
                      Specific questions get better answers: include the vehicle or ECU, what you've tried, and any exact codes.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <QuestionFormFields
                      value={formData}
                      errors={formErrors}
                      categories={categories}
                      onChange={setFormData}
                    />
                    <DialogFooter>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="bg-autoxium-blue hover:bg-autoxium-blue/90 text-white font-bold uppercase tracking-wider"
                        data-testid="submit-question-btn"
                      >
                        {submitting ? 'Posting...' : 'Post Question'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            ) : (
              <Link to="/login" state={{ from: '/community' }}>
                <Button variant="outline" className="font-bold uppercase tracking-wider" data-testid="login-to-ask-btn">
                  <LogIn className="h-4 w-4 mr-2" />
                  Login to ask a question
                </Button>
              </Link>
            )}
          </div>

          {activeCategory && (
            <p className="text-sm text-muted-foreground mb-6 max-w-2xl">{activeCategory.description}</p>
          )}

          {/* Category chips */}
          <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Filter by category" data-testid="category-filters">
            <button type="button" className={chipClass(!category)} onClick={() => updateParam('category', null)}>
              All{categories.length > 0 && ` (${allCount})`}
            </button>
            {categories.map((c) => (
              <button
                key={c.key}
                type="button"
                className={chipClass(category === c.key)}
                onClick={() => updateParam('category', c.key)}
                data-testid={`category-filter-${c.key}`}
              >
                {c.label} ({c.question_count})
              </button>
            ))}
          </div>

          {/* Status + sort */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-8 text-sm">
            <div className="flex items-center gap-1" role="group" aria-label="Filter by status">
              {STATUS_FILTERS.map((s) => (
                <Button
                  key={s.value}
                  size="sm"
                  variant={status === s.value ? 'default' : 'ghost'}
                  onClick={() => updateParam('status', s.value, 'all')}
                  data-testid={`status-filter-${s.value}`}
                >
                  {s.label}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-1" role="group" aria-label="Sort">
              <span className="text-muted-foreground text-xs uppercase tracking-wider mr-1">Sort</span>
              {SORTS.map((s) => (
                <Button
                  key={s.value}
                  size="sm"
                  variant={sort === s.value ? 'default' : 'ghost'}
                  onClick={() => updateParam('sort', s.value, 'newest')}
                  data-testid={`sort-${s.value}`}
                >
                  {s.label}
                </Button>
              ))}
            </div>
            {!loading && (
              <span className="text-muted-foreground text-xs sm:ml-auto">
                {total} question{total !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="technical-card">
                  <CardContent className="p-6">
                    <Skeleton className="h-6 w-full mb-2" />
                    <Skeleton className="h-4 w-full mb-4" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-destructive mb-4">{error}</p>
              <Button variant="outline" onClick={() => window.location.reload()}>Retry</Button>
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-16" data-testid="no-questions-message">
              <MessageSquare className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-chivo text-xl font-bold uppercase mb-2">No Questions Here Yet</h3>
              <p className="text-muted-foreground">
                {category || status !== 'all' ? 'Try a different filter, or start the discussion.' : 'Be the first to start a discussion.'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {questions.map((q) => (
                  <Link
                    key={q.question_id}
                    to={`/community/${q.question_id}`}
                    className="group min-w-0"
                    data-testid={`question-card-${q.question_id}`}
                  >
                    <Card className="technical-card h-full">
                      <CardContent className="p-6 flex flex-col h-full">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-wider">
                            {categoryLabel(q.category)}
                          </Badge>
                          {q.is_solved && (
                            <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white border-0 font-mono text-[10px] uppercase tracking-wider">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Solved
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-chivo text-lg font-bold mb-2 line-clamp-2 break-words group-hover:text-autoxium-blue transition-colors">
                          {q.title}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-2 break-words flex-1">
                          {q.description}
                        </p>
                        <div className="flex items-center justify-between gap-3 pt-4 border-t border-structure">
                          <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-muted-foreground min-w-0">
                            <span className="flex items-center gap-1 min-w-0">
                              <User className="h-3 w-3 shrink-0" />
                              <span className="truncate">{q.author_name}</span>
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(q.created_at)}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs font-mono shrink-0">
                            <span className="flex items-center gap-0.5 text-muted-foreground" title="Upvotes">
                              <ChevronUp className="h-3.5 w-3.5" />
                              {q.vote_count}
                            </span>
                            <span className="flex items-center gap-1 text-autoxium-blue" title="Answers">
                              <MessageSquare className="h-3 w-3" />
                              {q.answer_count}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
              {hasMore && (
                <div className="text-center mt-10">
                  <Button
                    variant="outline"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="font-bold uppercase tracking-wider"
                    data-testid="load-more-questions-btn"
                  >
                    {loadingMore ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default CommunityPage;
